// src/pages/Privacy.jsx
// 개인정보처리방침. YouTube API Services / Google API User Data Policy 고지 포함 (audit 필수).
// 법적 문서라 영어로 통일(국제 사용자 + 심사자). CONTACT_EMAIL 만 본인 것으로 확인/수정하면 됨.
import { Link } from 'react-router-dom';
import SEO from '../components/SEO.jsx';

const CONTACT_EMAIL = 'liger4903@gmail.com'; // TODO(citizencast): 운영 연락처로 확인/변경
const EFFECTIVE = '2026-08-28';

export default function Privacy() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-10 prose-sm">
      <SEO title="Privacy Policy" description="CitizenCast Privacy Policy" path="/privacy" />
      <h1 className="text-2xl font-bold mb-1">Privacy Policy</h1>
      <p className="text-xs text-gray-500 mb-6">Last updated: {EFFECTIVE}</p>

      <div className="space-y-5 text-sm text-gray-700 leading-relaxed">
        <p>
          CitizenCast (“we”, “us”, “the Service”) turns political news links into short vertical videos
          and, when you choose, uploads them to YouTube channels you own. This policy explains what data
          we collect, how we use it, and your choices.
        </p>

        <section>
          <h2 className="font-bold text-base">1. Information we collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account</strong>: your email and basic profile from your chosen sign-in provider (Google, Kakao, or LINE), and your selected country.</li>
            <li><strong>Usage</strong>: videos you generate, news links you submit, plan and credit balance, and referral code.</li>
            <li><strong>Payments</strong>: handled by our payment processor (Polar.sh, Merchant of Record). We do not store your card details.</li>
            <li><strong>YouTube connection</strong>: if you connect a YouTube channel, an OAuth token to upload videos on your behalf (see §3).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base">2. How we use your information</h2>
          <p>To authenticate you, generate and store your videos, process subscriptions and credits, operate the referral program, and—only when you connect a channel—upload videos to your YouTube channel.</p>
        </section>

        <section>
          <h2 className="font-bold text-base">3. Google / YouTube API Services</h2>
          <p>
            CitizenCast uses <strong>YouTube API Services</strong>. By using the YouTube features you also agree to the{' '}
            <a className="underline" href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">YouTube Terms of Service</a>.
            Information received from Google APIs is handled per the{' '}
            <a className="underline" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>, including the <strong>Limited Use</strong> requirements.
          </p>
          {/* ★ 2026-08-28 정정. 운영자 전용 전환 전에는 "고객이 채널을 연결한다" 고 적혀 있었는데,
              CitizenCast 에서 유튜브 연결은 처음부터 운영자 전용이었다(youtube-oauth-start 가 isAdmin 게이트,
              업로드 대상은 _youtube.js 의 고정 6채널). 방침이 사실과 달라 바로잡았다.
              AutoCast 컴플라이언스 회신에서 이 구조를 근거로 삼으므로 문구를 되돌리지 말 것. */}
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>YouTube channel connection is limited to the site operator.</strong> Users of this
              service do not connect a YouTube channel and no YouTube authorization is ever requested from
              them. The upload feature publishes only to a fixed set of channels the operator owns.</li>
            <li>The operator's OAuth refresh token is stored securely on our servers solely to perform those
              uploads. We do <strong>not</strong> sell, rent, or share this data, and do not use it for advertising.</li>
            <li>The operator can revoke that access at any time at{' '}
              <a className="underline" href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">Google Account → Third-party access</a>.</li>
          </ul>
          <p>See also the <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy Policy</a>.</p>
        </section>

        {/* ★ III.A.2g (2026-08-28). AutoCast 가 같은 항목으로 YouTube API 컴플라이언스 지적을 받았다.
            CitizenCast 도 GA4 쿠키·localStorage·sessionStorage·Firebase Auth 세션을 다 쓰면서
            방침에 한 줄도 없었다 — 같은 위반이라 함께 고쳤다. 실제 쓰는 키를 그대로 적는다.
            새 저장 키를 추가하면 이 목록도 같이 갱신할 것. */}
        <section>
          <h2 className="font-bold text-base">4. Cookies and similar technologies</h2>
          <p>
            CitizenCast stores and accesses information on your device using cookies, browser local storage,
            session storage, and IndexedDB. We use these for the purposes below — we do not use them for
            advertising, and we do not sell the data they hold.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Signing you in (required)</strong>: Firebase Authentication keeps your session in local
              storage / IndexedDB on your device so you stay logged in. Clearing it signs you out.
            </li>
            <li>
              <strong>Your preferences (required)</strong>: your selected country and interface language
              (<code>citizencast.country</code>, local storage), and a short-lived sign-in redirect note
              (<code>cc.authIntent</code>, session storage).
            </li>
            <li>
              <strong>Referral code</strong> (<code>cc.ref</code>, local storage): kept if you arrive through a
              referral link, so the referral can be credited.
            </li>
            <li>
              <strong>Analytics</strong>: Google Analytics 4 sets cookies (for example <code>_ga</code>) to measure
              aggregate page views and how the site is used. A local storage flag
              (<code>cc.ga_internal</code>) excludes the operator's own visits from those measurements. See the{' '}
              <a className="underline" href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noreferrer">
                Google statement on data use
              </a>. You can block these with your browser settings or the{' '}
              <a className="underline" href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer">
                Google Analytics opt-out add-on
              </a>.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> place cookies or store any data on your device on behalf of YouTube or
            Google APIs, and the operator's YouTube refresh token is stored on our servers only — never in any
            browser. You can clear the items above at any time through your browser&apos;s site-data settings;
            signing in again will recreate the required ones.
          </p>
        </section>

        <section>
          <h2 className="font-bold text-base">5. Storage &amp; security</h2>
          <p>Data is stored using Google Firebase (Authentication, Firestore, Storage) and processed on Cloudflare. Access tokens and secrets are kept server-side and never exposed to the browser.</p>
        </section>

        <section>
          <h2 className="font-bold text-base">6. Sharing</h2>
          <p>We share data only with service providers needed to run CitizenCast (e.g., Polar.sh for payments, OpenAI/ElevenLabs/Creatomate to produce videos). We never sell your personal data.</p>
        </section>

        <section>
          <h2 className="font-bold text-base">7. Retention &amp; deletion</h2>
          <p>You can delete your account and all associated data at any time from <Link className="underline" to="/account">Account → Delete account</Link>, which removes your videos, logo, and revokes connected channels. You may also email us to request deletion.</p>
        </section>

        <section>
          <h2 className="font-bold text-base">8. Children</h2>
          <p>CitizenCast is not directed to children under 13 (or the minimum age in your country).</p>
        </section>

        <section>
          <h2 className="font-bold text-base">9. Changes</h2>
          <p>We may update this policy; material changes will be posted on this page with a new date.</p>
        </section>

        <section>
          <h2 className="font-bold text-base">10. Contact</h2>
          <p>Questions or data requests: <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
        </section>

        <p className="pt-2"><Link className="underline" to="/terms">Terms of Service →</Link></p>
      </div>
    </div>
  );
}
